import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import KildenMap from './KildenMap';
import { ErrorBoundary } from 'react-error-boundary';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { Helmet } from 'react-helmet'; // Add this import
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';  // Add this import
import { Tab } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Switch } from '@headlessui/react';  // Add this import
import { ChevronDownIcon } from '@heroicons/react/20/solid'; // Add this import for dropdown icon

// Add error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
        <div className="p-4 bg-red-500/10 rounded-lg">
            <h2 className="text-xl font-bold mb-2">Something went wrong:</h2>
            <pre className="text-sm text-red-200">{error.message}</pre>
            <button 
                onClick={resetErrorBoundary}
                className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
                Try again
            </button>
        </div>
    );
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    annotationPlugin  // Register the plugin this way
);

// Add this helper function at the top
const validatePriceData = (data) => {
    if (!data || !data.hourly || !Array.isArray(data.hourly)) {
        return false;
    }
    return true;
};

// Norwegian VAT rate for electricity
const VAT_RATE = 0.25; // 25%

// Add this helper function to apply VAT
const applyVat = (price, includeVat) => {
    if (price === null || price === undefined) return null;
    return includeVat ? price * (1 + VAT_RATE) : price;
};

// Add this helper function near the other helper functions at the top
const formatPrice = (price) => {
    return price?.toString().replace('.', ',') ?? '-';
};

// Add these helper functions after validatePriceData
const getCurrentPrice = (hourlyPrices, includeVat) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    const currentHour = new Date().getHours();
    return applyVat(hourlyPrices[currentHour], includeVat);
};

const getLowestPrice = (hourlyPrices, includeVat) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    const withVat = hourlyPrices.map(p => applyVat(p, includeVat));
    const lowest = Math.min(...withVat);
    const hour = withVat.indexOf(lowest);
    return { price: lowest, hour };
};

const getHighestPrice = (hourlyPrices, includeVat) => {
    if (!validatePriceData({ hourly: hourlyPrices })) return null;
    const withVat = hourlyPrices.map(p => applyVat(p, includeVat));
    const highest = Math.max(...withVat);
    const hour = withVat.indexOf(highest);
    return { price: highest, hour };
};

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000;

const pricingStyles = {
    container: {
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderRadius: '15px',
        padding: '20px',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
        color: '#333',
        fontFamily: 'Arial, sans-serif',
        transition: 'background 0.5s ease',
    },
    header: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#333',
    },
    gradientBackground: {
        background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
        animation: 'gradient 3s ease infinite',
    },
    '@keyframes gradient': {
        '0%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
        '100%': { backgroundPosition: '0% 50%' },
    },
    interactiveElement: {
        cursor: 'pointer',
        transition: 'transform 0.2s',
    },
    interactiveElementHover: {
        transform: 'scale(1.05)',
    },
};

const ElectricityPricingContent = ({
    defaultRegion = 'NO1',
    regions = ['NO1', 'NO2', 'NO3', 'NO4', 'NO5'],
    regionColors = {
        'NO1': '#0f415e',
        'NO2': '#0f415e',
        'NO3': '#0f415e',
        'NO4': '#0f415e',
        'NO5': '#0f415e'
    },
    getRegionColorClass = (region) => {
        const colors = {
            'NO1': 'bg-green-500',
            'NO2': 'bg-blue-500',
            'NO3': 'bg-yellow-500',
            'NO4': 'bg-red-500',
            'NO5': 'bg-brown-500'
        };
        return colors[region] || 'bg-gray-500';
    },
    MapComponent = KildenMap,
    apiEndpoint = '/api/norwegian-prices',
    countryName = 'Norge'
}) => {
    const [data, setData] = useState({ today: '', Norwayprices: {} });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(defaultRegion);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [predictedPrices, setPredictedPrices] = useState({ tomorrow: {} });  // Remove dayAfterTomorrow
    const [selectedDate, setSelectedDate] = useState('today');
    const [includeVat, setIncludeVat] = useState(false); // Default to excluding VAT

    useEffect(() => {
        const fetchPricesWithRetry = async (retryCount = 0) => {
            try {
                setLoading(true);
                const response = await axios.get(`${process.env.REACT_APP_API_URL}${apiEndpoint}`);
                
                // Normalize the data structure
                const normalizedData = {
                    today: response.data.today || '',
                    Norwayprices: response.data.Norwayprices || {}
                };

                setData(normalizedData);
                const predictionResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/norwegian-prices/predictions`);
                setPredictedPrices(predictionResponse.data);
                setError(null);
                window.dispatchEvent(new Event('electricityPricingLoaded'));
            } catch (err) {
                console.error(`Attempt ${retryCount + 1} failed:`, err);
                if (retryCount < MAX_RETRIES) {
                    setTimeout(() => fetchPricesWithRetry(retryCount + 1), RETRY_DELAY);
                } else {
                    setError('Could not load electricity prices. Please try again later.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchPricesWithRetry();
    }, [apiEndpoint]);

    if (error) return <div>{error}</div>;

    const generateChartData = (prices) => {
        const hours = Array.from({ length: 24 }, (_, i) => `${i}:00`);
        
        const datasets = Object.entries(prices).flatMap(([region, regionData]) => {
            if (!regionData || !Array.isArray(regionData.hourly)) {
                console.error(`Skipping region ${region} due to invalid data`, regionData);
                return [];
            }

            const paddedData = [...regionData.hourly];
            while (paddedData.length < 24) paddedData.push(null);

            return {
                label: region,
                // Show price in øre/kWh (multiply by 100 to convert from NOK to øre)
                data: paddedData.map(price => 
                    price !== null 
                        ? (applyVat(price, includeVat) * 100).toFixed(2) 
                        : null
                ),
                borderColor: regionColors[region],
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    
                    if (!chartArea) return regionColors[region] + '20'; // Fallback

                    const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                    gradient.addColorStop(0, `${regionColors[region]}40`); // 25% opacity at top
                    gradient.addColorStop(1, `${regionColors[region]}00`); // 0% opacity at bottom
                    return gradient;
                },
                fill: 'start',
                stepped: 'before',
                tension: 0.4,
            };
        });

        return { labels: hours, datasets };
    };

    const getCurrentDataset = () => {
        switch (selectedDate) {
            case 'tomorrow':
                return data.predictedPrices?.tomorrow || {};
            default:
                return data.Norwayprices;
        }
    };

    const getFormattedDate = () => {
        const date = new Date();
        switch (selectedDate) {
            case 'tomorrow':
                date.setDate(date.getDate() + 1);
                break;
            default:
                break;
        }
        const formatted = date.toLocaleDateString('nb-NO', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        // Check if there's already a period after the day number and handle accordingly
        return formatted.includes('.') ? formatted : formatted.replace(/(\d+)/, '$1.');
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        resizeDelay: 100,
        events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'], // Limit event listeners
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            annotation: {
                annotations: {
                    currentTime: {
                        xScaleID: 'x', // add this line
                        display: selectedDate === 'today', // Only show for today
                        type: 'line',
                        xMin: new Date().getHours(),
                        xMax: new Date().getHours(),
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        borderWidth: 2,
                        borderDash: [6, 6],
                        label: {
                            display: true,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            content: (ctx) => {
                                const currentPrice = getCurrentPrice(data.Norwayprices[selectedRegion]?.hourly, includeVat);
                                return [`Nå`, `${formatPrice((currentPrice * 100)?.toFixed(2))} øre/kWh`];
                            },
                            position: 'start',
                            padding: 6,
                            font: {
                                size: 12
                            },
                            // Improve safety checks for positioning
                            xAdjust: 0,
                            yAdjust: 0,
                            // Use a safer approach to handle undefined values
                            overflow: 'fit',
                            enabled: (ctx) => {
                                // Only enable if chart is properly initialized
                                return !!(ctx && ctx.chart && ctx.chart.chartArea && 
                                        typeof ctx.chart.chartArea.left === 'number' && 
                                        typeof ctx.chart.chartArea.right === 'number');
                            },
                        },
                        // Improve the safety check in beforeDraw
                        beforeDraw: (ctx) => {
                            // Skip drawing if chart isn't properly initialized
                            if (!ctx || !ctx.chart || !ctx.chart.chartArea || 
                                typeof ctx.chart.chartArea.left === 'undefined') {
                                return false;
                            }
                            return true;
                        },
                        // Add a new callback to create annotation only when chart is ready
                        afterDraw: (ctx) => {
                            return !!ctx?.chart?.chartArea;
                        }
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                titleFont: {
                    size: 14,
                    family: 'system-ui'
                },
                bodyFont: {
                    size: 13,
                    family: 'system-ui'
                },
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                callbacks: {
                    label: function(tooltipItem) {
                        const timeRange = `${tooltipItem.label}–${(parseInt(tooltipItem.label) + 1) % 24}:00`;
                        const currentHour = new Date().getHours();
                        const isCurrentHour = parseInt(tooltipItem.label) === currentHour;
                        
                        let label = `${tooltipItem.dataset.label}: ${formatPrice(tooltipItem.raw)} øre/kWh`;
                        if (isCurrentHour) {
                            label += ' (Nåværende pris)';
                        }
                        return `${label} (${timeRange})`;
                    },
                    title: function(tooltipItems) {
                        const item = tooltipItems[0];
                        const hour = parseInt(item.label);
                        const currentHour = new Date().getHours();
                        if (hour === currentHour) {
                            return `${item.label}:00 (Nå)`;
                        }
                        return `${item.label}:00`;
                    }
                }
            },
            legend: {
                display: false, // Hide the legend completely - this removes the flat square
            }
        },
        scales: {
            y: {
                border: {
                    display: false
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.05)',
                    drawTicks: false
                },
                ticks: {
                    color: '#333333',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    callback: function(value) {
                        return formatPrice(value.toFixed(1)) + ' øre';
                    }
                },
                // Add better auto-scaling for mobile
                beginAtZero: false,
                suggestedMin: function(context) {
                    // Calculate a more appropriate min value based on data
                    if (!context.chart.data.datasets || !context.chart.data.datasets[0]) return 0;
                    
                    // Find the minimum value across all datasets
                    const allValues = context.chart.data.datasets
                        .flatMap(dataset => dataset.data)
                        .filter(val => val !== null && val !== undefined);
                    
                    if (allValues.length === 0) return 0;
                    const min = Math.min(...allValues);
                    // Return slight offset below min for better visualization
                    return Math.max(0, min * 0.85);
                },
                suggestedMax: function(context) {
                    // Calculate a more appropriate max value based on data
                    if (!context.chart.data.datasets || !context.chart.data.datasets[0]) return 100;
                    
                    // Find the maximum value across all datasets
                    const allValues = context.chart.data.datasets
                        .flatMap(dataset => dataset.data)
                        .filter(val => val !== null && val !== undefined);
                    
                    if (allValues.length === 0) return 100;
                    const max = Math.max(...allValues);
                    // Return slight offset above max for better visualization
                    return max * 1.15;
                }
            },
            x: {
                border: {
                    display: false
                },
                grid: {
                    display: false
                },
                ticks: {
                    color: 'rgba(0, 0, 0, 0.7)',
                    font: {
                        size: 12,
                        family: 'system-ui'
                    },
                    padding: 10,
                    // Only show every third hour label on small screens
                    maxRotation: 0,
                    callback: function(value, index) {
                        // On mobile screens show fewer labels
                        if (window.innerWidth < 768) {
                            return index % 3 === 0 ? value : '';
                        }
                        return value;
                    }
                }
            }
        }
    };

    const MemoizedChart = React.memo(({ data, options }) => (
        <Line data={data} options={options} redraw={false} />
    ));

    const formattedStr = data.today ? `${data.today.slice(0, 4)}-${data.today.slice(4, 6)}-${data.today.slice(6, 8)}` : '';
    const formattedDate = formattedStr ? (() => {
        const formatted = new Date(formattedStr).toLocaleDateString('nb-NO', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        return formatted.includes('.') ? formatted : formatted.replace(/(\d+)/, '$1.');
    })() : 'Invalid date';

    const handleRegionSelect = (region) => {
        setSelectedRegion(region);
        console.log(`Region selected: ${region}`);
    };

    // Add region dropdown change handler
    const handleRegionChange = (e) => {
        handleRegionSelect(e.target.value);
    };

    // Improve the getChartHeight function for better mobile experience
    const getChartHeight = () => {
        // Much taller on mobile devices, especially Chrome
        if (window.innerWidth < 480) {
            return "h-[400px]"; // Significantly taller on small mobile
        } else if (window.innerWidth < 768) {
            return "h-[450px]"; // Taller on larger mobile
        }
        return "h-80 md:h-[calc(100%-80px)]"; // Original height on desktop
    };

    // Add a function to determine if we're on mobile Chrome
    const isMobileChrome = () => {
        return navigator.userAgent.indexOf('Chrome') !== -1 && window.innerWidth < 768;
    };

    // Add error boundary for chart rendering
    const renderChart = () => {
        try {
            const chartData = generateChartData(
                selectedRegion === 'ALL' 
                    ? getCurrentDataset() 
                    : { [selectedRegion]: getCurrentDataset()[selectedRegion] }
            );

            if (!chartData || !chartData.datasets || !chartData.labels) {
                console.error('Invalid chart data:', chartData);
                return <div>Error: Invalid data format</div>;
            }

            // Enhanced mobile Chrome options
            if (isMobileChrome()) {
                const mobileOptions = {
                    ...chartOptions,
                    maintainAspectRatio: false,
                    layout: {
                        padding: {
                            top: 10,
                            right: 10,
                            bottom: 10,
                            left: 10
                        }
                    },
                    plugins: {
                        ...chartOptions.plugins,
                        annotation: {
                            annotations: {} // Empty annotations on mobile Chrome
                        }
                    },
                    scales: {
                        ...chartOptions.scales,
                        y: {
                            ...chartOptions.scales.y,
                            // Force a more expanded range for mobile
                            ticks: {
                                ...chartOptions.scales.y.ticks,
                                count: 5, // Limit number of ticks for clarity
                            },
                            // More aggressive adjustment for mobile
                            suggestedMin: function(context) {
                                if (!context.chart.data.datasets || !context.chart.data.datasets[0]) return 0;
                                
                                const allValues = context.chart.data.datasets
                                    .flatMap(dataset => dataset.data)
                                    .filter(val => val !== null && val !== undefined)
                                    .map(val => parseFloat(val));
                                
                                if (allValues.length === 0) return 0;
                                const min = Math.min(...allValues);
                                return Math.max(0, min * 0.7); // More space below on mobile
                            },
                            suggestedMax: function(context) {
                                if (!context.chart.data.datasets || !context.chart.data.datasets[0]) return 100;
                                
                                const allValues = context.chart.data.datasets
                                    .flatMap(dataset => dataset.data)
                                    .filter(val => val !== null && val !== undefined)
                                    .map(val => parseFloat(val));
                                
                                if (allValues.length === 0) return 100;
                                const max = Math.max(...allValues);
                                return max * 1.3; // More space above on mobile
                            }
                        }
                    }
                };

                return (
                    <div className="relative w-full h-full flex items-center justify-center">
                        <MemoizedChart 
                            data={chartData} 
                            options={mobileOptions}
                            className="w-full h-full" 
                        />
                    </div>
                );
            }

            return (
                <div className="w-full h-full">
                    <MemoizedChart 
                        data={chartData} 
                        options={chartOptions}
                        className="w-full h-full"
                    />
                </div>
            );
        } catch (error) {
            console.error('Chart rendering error:', error);
            return <div>Error rendering chart</div>;
        }
    };

    return (
        <div className="min-h-screen bg-[#f0f0f0]">
            <div className="container mx-auto px-4 py-8 max-w-6xl">
                {/* Mobile Region Dropdown */}
                <div className="md:hidden mb-3">
                    <label htmlFor="region-select" className="block text-sm font-medium text-[#0f415e] mb-2">
                        Velg prisområde
                    </label>
                    <div className="relative">
                        <select
                            id="region-select"
                            value={selectedRegion}
                            onChange={handleRegionChange}
                            className="block w-full appearance-none bg-white border border-gray-200 text-[#0f415e] py-3 px-4 pr-8 rounded-xl leading-tight focus:outline-none focus:ring-2 focus:ring-[#0f415e]/30"
                        >
                            {Object.keys(data.Norwayprices || {}).map((area) => (
                                <option key={area} value={area}>
                                    {area} - {formatPrice((applyVat(parseFloat(data.Norwayprices[area]?.average || 0), includeVat) * 100).toFixed(1))} øre/kWh
                                </option>
                            ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-[#0f415e]">
                            <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                        </div>
                    </div>
                </div>
                
                {/* Updated Price Cards Grid - Hidden on mobile */}
                <div className="hidden md:grid grid-cols-3 lg:grid-cols-5 gap-2 mb-6">
                    {!loading && Object.entries(data.Norwayprices).map(([area, priceData]) => (
                        <button
                            key={area}
                            onClick={() => handleRegionSelect(area)}
                            className={`
                                p-2 rounded-xl transition-all duration-300
                                ${selectedRegion === area 
                                    ? 'bg-[#0f415e] text-white shadow-lg scale-102' 
                                    : 'bg-white text-[#0f415e] hover:bg-[#0f415e] hover:text-white'}
                            `}
                        >
                            <h3 className="font-medium mb-1">
                                {area}
                            </h3>
                            <p className="text-lg font-light">
                                {/* Convert NOK to øre (multiply by 100) and apply VAT if needed */}
                                {formatPrice((applyVat(parseFloat(priceData?.average || 0), includeVat) * 100).toFixed(1))}
                                <span className="text-xs ml-1 opacity-60">øre/kWh</span>
                            </p>
                        </button>
                    ))}
                </div>

                {/* Main Content Grid - Restructured for equal height */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* Chart Area - Takes 8 of 12 columns */}
                    <div className="lg:col-span-8">
                        {/* Chart Card - No bottom margin */}
                        <div className="bg-white rounded-xl p-4 md:p-6 h-full flex flex-col">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 md:mb-6">
                                <h2 className="text-xl md:text-2xl font-light text-[#7c7f82] mb-4 md:mb-0">
                                    {getFormattedDate()}
                                </h2>
                                <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                                    {/* VAT Toggle */}
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-medium text-[#7c7f82]">
                                            {includeVat ? 'Inkl. MVA (25%)' : 'Ekskl. MVA'}
                                        </span>
                                        <Switch
                                            checked={includeVat}
                                            onChange={setIncludeVat}
                                            className={`${
                                                includeVat ? 'bg-[#0f415e]' : 'bg-[#eff2f3]'
                                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0f415e]/20`}
                                        >
                                            <span
                                                className={`${
                                                    includeVat ? 'translate-x-6' : 'translate-x-1'
                                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                                            />
                                        </Switch>
                                    </div>
                                    
                                    {/* Date Selector */}
                                    {/* <div className="inline-flex bg-[#eff2f3] rounded-lg p-1">
                                        {['today', 'tomorrow'].map((date) => (
                                            <button
                                                key={date}
                                                onClick={() => setSelectedDate(date)}
                                                className={`
                                                    px-4 py-2 rounded-md text-sm transition-all
                                                    ${selectedDate === date 
                                                        ? 'bg-white text-[#7c7f82] shadow-sm' 
                                                        : 'text-[#7c7f82]/60 hover:text-[#7c7f82]'}
                                                `}
                                            >
                                                {date === 'today' ? 'Idag' : 'I morgen'}
                                            </button>
                                        ))}
                                    </div> */}
                                </div>
                            </div>
                            <div className={`${getChartHeight()} flex-grow`}> {/* Added flex-grow to fill available space */}
                                {loading ? (
                                    <div className="animate-pulse h-full bg-[#eff2f3] rounded-lg" />
                                ) : (
                                    renderChart()
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Map Section - Takes 4 of 12 columns */}
                    <div className="hidden lg:block lg:col-span-4">
                        <div className="bg-white rounded-xl p-4 md:p-6 h-full"> {/* Equal height with chart */}
                            <h3 className="text-xl font-light text-[#7c7f82] mb-6">Prisområder</h3>
                            <div className="h-[calc(100%-50px)]"> {/* Dynamic height calculation */}
                                <MapComponent 
                                    onRegionSelect={handleRegionSelect} 
                                    selectedRegion={selectedRegion}
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Price Cards - Full width below the chart and map */}
                    <div className="lg:col-span-12">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="bg-white/80 backdrop-blur rounded-xl p-3 md:p-4 transition-all hover:bg-white">
                                <h3 className="text-[#7c7f82] font-medium mb-1">Nåværende pris</h3>
                                <p className="text-2xl font-light text-[#7c7f82]">
                                    {formatPrice((getCurrentPrice(data.Norwayprices[selectedRegion]?.hourly, includeVat) * 100)?.toFixed(2))}
                                    <span className="text-sm ml-1 opacity-60">øre/kWh</span>
                                    <span className="text-xs ml-2 opacity-50">
                                        {includeVat ? '(inkl. MVA)' : '(ekskl. MVA)'}
                                    </span>
                                </p>
                            </div>
                            <div className="bg-white/80 backdrop-blur rounded-xl p-3 md:p-4 transition-all hover:bg-white">
                                <h3 className="text-[#7c7f82] font-medium mb-1">Laveste pris</h3>
                                <p className="text-2xl font-light text-[#7c7f82]">
                                    {formatPrice((getLowestPrice(data.Norwayprices[selectedRegion]?.hourly, includeVat)?.price * 100)?.toFixed(2))}
                                    <span className="text-sm ml-1 opacity-60">øre/kWh</span>
                                    <span className="text-xs ml-2 opacity-50">
                                        {includeVat ? '(inkl. MVA)' : '(ekskl. MVA)'}
                                    </span>
                                </p>
                            </div>
                            <div className="bg-white/80 backdrop-blur rounded-xl p-3 md:p-4 transition-all hover:bg-white">
                                <h3 className="text-[#7c7f82] font-medium mb-1">Høyeste pris</h3>
                                <p className="text-2xl font-light text-[#7c7f82]">
                                    {formatPrice((getHighestPrice(data.Norwayprices[selectedRegion]?.hourly, includeVat)?.price * 100)?.toFixed(2))}
                                    <span className="text-sm ml-1 opacity-60">øre/kWh</span>
                                    <span className="text-xs ml-2 opacity-50">
                                        {includeVat ? '(inkl. MVA)' : '(ekskl. MVA)'}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Wrap main component with ErrorBoundary
const ElectricityPricing = (props) => {
    return (
        <ErrorBoundary 
            FallbackComponent={ErrorFallback}
            onReset={() => window.location.reload()}
        >
            <Helmet>
                <title>Kildenkraft</title>
                <link rel="canonical" href="https://kildenkraft.no/strompriser/" />
            </Helmet>
            <ElectricityPricingContent {...props} />
        </ErrorBoundary>
    );
};

// Export the component wrapped in React.memo
export default React.memo(ElectricityPricing);

// Add this helper function for region colors
const getRegionColorClass = (region) => {
    const colors = {
        'NO1': 'bg-green-500',
        'NO2': 'bg-blue-500',
        'NO3': 'bg-yellow-500',
        'NO4': 'bg-red-500',
        'NO5': 'bg-brown-500'
    };
    return colors[region] || 'bg-gray-500';
};

export const PriceCards = ({ data, loading, selectedRegion, getRegionColorClass, getCurrentPrice, getLowestPrice, getHighestPrice, formatHour, includeVat }) => {
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {loading ? (
                Array(3).fill(0).map((_, index) => (
                    <div key={index} className="animate-pulse bg-white/5 rounded-lg h-[80px] md:h-[100px]" />
                ))
            ) : (
                <>
                    <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-sm md:text-base font-bold mb-1">Nåværende pris</h3>
                                <p className="text-xs md:text-sm text-white/50">
                                    For øyeblikket {includeVat ? '(inkl. MVA)' : '(ekskl. MVA)'}
                                </p>
                            </div>
                            <p className="text-lg md:text-xl font-bold">
                                {formatPrice(getCurrentPrice(data.Norwayprices[selectedRegion]?.hourly, includeVat) * 100?.toFixed(1) ?? '-')}
                                <span className="text-xs md:text-sm opacity-70 ml-1">øre/kWh</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-sm md:text-base font-bold mb-1">Laveste pris</h3>
                                <p className="text-xs md:text-sm text-white/50">
                                    Kl. {formatHour(getLowestPrice(data.Norwayprices[selectedRegion]?.hour, includeVat)?.hour)}
                                    {includeVat ? ' (inkl. MVA)' : ' (ekskl. MVA)'}
                                </p>
                            </div>
                            <p className="text-lg md:text-xl font-bold">
                                {formatPrice((getLowestPrice(data.Norwayprices[selectedRegion]?.hourly, includeVat)?.price * 100).toFixed(1) ?? '-')}
                                <span className="text-xs md:text-sm opacity-70 ml-1">øre/kWh</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 bg-white/5 backdrop-blur-lg border border-white/10 rounded-lg p-3">
                        <div className={`h-1 w-full rounded-t-lg ${getRegionColorClass(selectedRegion)} mb-2`}></div>
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="text-sm md:text-base font-bold mb-1">Høyeste pris</h3>
                                <p className="text-xs md:text-sm text-white/50">
                                    Kl. {formatHour(getHighestPrice(data.Norwayprices[selectedRegion]?.hour, includeVat)?.hour)}
                                    {includeVat ? ' (inkl. MVA)' : ' (ekskl. MVA)'}
                                </p>
                            </div>
                            <p className="text-lg md:text-xl font-bold">
                                {formatPrice((getHighestPrice(data.Norwayprices[selectedRegion]?.hourly, includeVat)?.price * 100).toFixed(1) ?? '-')}
                                <span className="text-xs md:text-sm opacity-70 ml-1">øre/kWh</span>
                            </p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
